import React, { useState } from "react";
import { NumberInput } from "../NumberInput/NumberInput.tsx";
import { Root, Track, Range, Thumb } from "@radix-ui/react-slider";
import { useControlled } from "@exonn/hooks";
import * as styles from "./RangeInput.styles.tsx";
import type { SliderProps, SliderThumbProps } from "@radix-ui/react-slider";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

interface IProps extends Omit<SliderProps, "dir" | "name" | "orientation"> {
  label?: string;
  helperText?: string;
  startThumbProps?: SliderThumbProps;
  endThumbProps?: SliderThumbProps;
}

type Values = [number, number];

export const RangeInput = (props: IProps) => {
  const {
    label,
    helperText,
    min = 0,
    max = 100,
    step = 1,
    minStepsBetweenThumbs,
    value: valueProp,
    defaultValue = [min, max],
    disabled = false,
    onValueChange,
    onValueCommit,
    startThumbProps,
    endThumbProps,
  } = props;

  const [committedValue, setCommittedValue] = useState<Values>([0, 0]);

  const [value, setValue] = useControlled({
    name: "Counter",
    state: "count",
    controlled: valueProp,
    default: defaultValue,
    onChange: onValueChange,
  });

  const handleSliderChange = (newValue: Values) => {
    setValue(newValue);
  };

  const handleSliderCommit = (newValue: Values) => {
    setCommittedValue(newValue);
    setValue(newValue);
    onValueCommit?.(newValue);
  };

  const createInputChangeHandler =
    (i: 0 | 1) => (newValue: string | number | null) => {
      if (typeof newValue === "string") newValue = parseFloat(newValue);
      if (newValue === null || isNaN(newValue)) return;

      const nextValue = i === 0 ? [newValue, value[1]] : [value[0], newValue];

      handleSliderCommit(nextValue as Values);
    };

  const handleLostPointer = () => {
    // sometimes onValueCommit is not fired
    // we will make sure this event fires consistently
    // https://github.com/radix-ui/primitives/issues/1760
    if (!areRangesEqual(value as Values, committedValue)) {
      handleSliderCommit(value as Values);
    }
  };

  return (
    <div css={styles.rootContainer}>
      {label && <InputLabel label={label} helperText={helperText} />}

      <div css={styles.numberInputsContainer}>
        <NumberInput
          controls={false}
          value={value[0]}
          disabled={disabled}
          onChange={createInputChangeHandler(0)}
        />
        <div css={styles.hyphen} />
        <NumberInput
          controls={false}
          value={value[1]}
          disabled={disabled}
          onChange={createInputChangeHandler(1)}
        />
      </div>

      <Root
        css={styles.sliderRoot}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommit}
        {...{
          min,
          max,
          step,
          minStepsBetweenThumbs,
          value,
          disabled,
        }}
      >
        <Track css={styles.sliderTrack({ disabled })}>
          <Range css={styles.sliderRange({ disabled })} />
        </Track>
        <Thumb
          css={styles.sliderThumb({ disabled })}
          onLostPointerCapture={handleLostPointer}
          {...startThumbProps}
        />

        <Thumb
          css={styles.sliderThumb({ disabled })}
          onLostPointerCapture={handleLostPointer}
          {...endThumbProps}
        />
      </Root>
    </div>
  );
};

function areRangesEqual(a: Values, b: Values) {
  return a[0] === b[0] && a[1] === b[1];
}
